import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import DefaultLayout from "./layouts/Default";
import NonAuthLayout from "./layouts/NonAuth";
import { AuthProtected } from "./routes/AuthProtected";
import { OnboardingProtected } from "./routes/OnboardingProtected";
import {
  publicRoutes,
  privateRoutes,
  onboardingRoutes,
} from "./routes/allRoutes";
import NotFoundPage from "./pages/NotFound/NotFoundPage";
import { initGA, initContentsquare, logPageView } from "./utils/analytics";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { useBeforeRender } from "./utils/useBeforeRender";
import { usePageTitle } from "./hooks/usePageTitle";
import { initializeLanguage } from "./utils/initializeLanguage";
import { initializeDataIntegrity } from "./utils/dataIntegrity";

import "bootstrap/dist/css/bootstrap.min.css";
import "./assets/scss/theme.scss";
import "./assets/scss/chat.scss";
import { GlobalNotificationProvider } from "./components/GlobalNotificationProvider";
import { createAppTheme } from "./theme/theme";
import LoadingScreen from "./components/LoadingScreen";
import ErrorBoundary from "./components/ErrorBoundary";
import {
  GlobalThemeProvider,
  useGlobalTheme,
} from "./context/GlobalThemeContext";
import { FeatureFlagsProvider } from "./contexts/FeatureFlagsContext";

/** Reads the global dark-mode flag and feeds the matching MUI theme downward. */
const DynamicThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isDark = useGlobalTheme();
  const appTheme = useMemo(() => createAppTheme(isDark), [isDark]);
  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

const App = () => {
  const [splash, setSplash] = useState(true);

  useBeforeRender(() => {
    window.addEventListener("error", (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div",
        );
        const resizeObserverErrOverlay = document.getElementById(
          "webpack-dev-server-client-overlay",
        );
        if (resizeObserverErrDiv)
          resizeObserverErrDiv.className = "hide-resize-observer";
        if (resizeObserverErrOverlay)
          resizeObserverErrOverlay.className = "hide-resize-observer";
      }
    });
  }, []);

  const location = useLocation();

  useEffect(() => {
    initGA();
    initContentsquare();
    initializeLanguage();
    initializeDataIntegrity();
  }, []);

  useEffect(() => {
    logPageView();
  }, [location]);

  usePageTitle();

  useEffect(() => {
    const t = setTimeout(() => setSplash(false), 1500);
    return () => clearTimeout(t);
  }, []);

  if (splash) {
    return <LoadingScreen />;
  }
  return (
    <ErrorBoundary>
      <GlobalThemeProvider>
        <DynamicThemeProvider>
          <GlobalNotificationProvider>
            <FeatureFlagsProvider>
              <Routes>
                {publicRoutes.map((route: any, idx: number) => (
                  <Route
                    path={route.path}
                    element={<NonAuthLayout>{route.component}</NonAuthLayout>}
                    key={idx}
                  />
                ))}
                {privateRoutes.map((route: any, idx: number) => (
                  <Route
                    path={route.path}
                    element={
                      <AuthProtected>
                        <DefaultLayout>{route.component}</DefaultLayout>
                      </AuthProtected>
                    }
                    key={idx}
                  />
                ))}
                {onboardingRoutes.map((route: any, idx: number) => (
                  <Route
                    path={route.path}
                    element={
                      <OnboardingProtected>
                        <NonAuthLayout>{route.component}</NonAuthLayout>
                      </OnboardingProtected>
                    }
                    key={idx}
                  />
                ))}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </FeatureFlagsProvider>
          </GlobalNotificationProvider>
        </DynamicThemeProvider>
      </GlobalThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
